import {
  BarChartOutlined,
  CloudDownloadOutlined,
  FileTextOutlined,
  HomeOutlined,
  LoginOutlined,
  LogoutOutlined,
  MessageOutlined,
  PercentageOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  TeamOutlined,
  TruckOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { findLanguage } from '@mahawi/eshop-common/src/translation';
import { type ILanguageType } from '@mahawi/eshop-common/src/types';
import { Badge, Menu, Select, Switch } from 'antd';
import { type MenuItemType } from 'antd/es/menu/interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { setLanguage } from '../reducers/language/reducer';
import { useTheme } from '../theme/theme-provider';
import Link from './link';

function Topbar(): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageLanguages = useSelector((s: RootState) => s.Language.languages);
  const languageTypeCode = useSelector(
    (s: RootState) => s.Language.languageType?.code,
  );
  const userIsLoggedIn = useSelector((s: RootState) => s.User.isLoggedIn);
  const withdrawalReceivedCount = useSelector(
    (s: RootState) => s.Withdrawal.receivedCount,
  );

  const { isDark, toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);

  const onChangeSelect = useCallback(
    (code: string): void => {
      const languageType: ILanguageType | null = findLanguage(
        languageLanguages,
        code,
      );

      if (languageType) {
        dispatch(setLanguage(languageType));
        searchParams.set('languageTypeCode', languageType.code);
        setSearchParams(searchParams);
      }
    },
    [dispatch, languageLanguages, searchParams, setSearchParams],
  );

  useEffect(() => {
    const code: string | null = searchParams.get('languageTypeCode');

    if (!code) {
      return;
    }

    const languageType: ILanguageType | null = findLanguage(
      languageLanguages,
      code,
    );

    if (languageType) {
      dispatch(setLanguage(languageType));
    }
  }, [searchParams, setSearchParams, languageLanguages, dispatch]);

  useEffect((): void => {
    let menuItemsUE: MenuItemType[] = [
      {
        key: 'home',
        icon: <HomeOutlined />,
        label: <Link to="/" label="Home" />,
      },
    ];

    if (userIsLoggedIn) {
      menuItemsUE = menuItemsUE.concat([
        {
          key: 'dashboard',
          icon: <BarChartOutlined />,
          label: <Link to="/dashboard" label="Dashboard" />,
        },
        {
          key: 'offer',
          icon: <TagsOutlined />,
          label: <Link to="/offer" label="Offer" />,
        },
        {
          key: 'orders',
          icon: <ShoppingCartOutlined />,
          label: (
            <Link
              to="/orders"
              label={
                <Badge
                  count={withdrawalReceivedCount}
                  size="small"
                  offset={[10, 0]}
                >
                  Orders
                </Badge>
              }
            />
          ),
        },
        {
          key: 'packets',
          icon: <TruckOutlined />,
          label: <Link to="/packets" label="Shipments" />,
        },
        {
          key: 'messenger',
          icon: <MessageOutlined />,
          label: <Link to="/messenger" label="Messenger" />,
        },
        {
          key: 'discount',
          icon: <PercentageOutlined />,
          label: <Link to="/discount" label="Discount" />,
        },
        {
          key: 'downloader',
          icon: <CloudDownloadOutlined />,
          label: <Link to="/downloader" label="Downloader" />,
        },
        {
          key: 'ebay',
          icon: <ShopOutlined />,
          label: <Link to="/ebay" label="eBay" />,
        },
        {
          key: 'marketing',
          icon: <RocketOutlined />,
          label: <Link to="/marketing" label="Marketing" />,
        },
        {
          key: 'report',
          icon: <FileTextOutlined />,
          label: <Link to="/report" label="Report" />,
        },
        {
          key: 'settings',
          icon: <SettingOutlined />,
          label: <Link to="/settings" label="Settings" />,
        },
        {
          key: 'crm',
          icon: <TeamOutlined />,
          label: <Link to="/crm" label="CRM" />,
        },
        {
          key: 'search',
          icon: <SearchOutlined />,
          label: <Link to="/search" label="Search" />,
        },
        {
          key: 'account',
          icon: <UserOutlined />,
          label: <Link to="/account" label="Account" />,
        },
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: <Link to="/logout" label="Logout" />,
        },
      ]);
    } else {
      menuItemsUE = menuItemsUE.concat([
        {
          key: 'login',
          icon: <LoginOutlined />,
          label: <Link to="/login" label="Login" />,
        },
      ]);
    }

    menuItemsUE = menuItemsUE.concat([
      {
        key: 'language',
        label: (
          <Select
            placeholder="Select language"
            onChange={(value: string): void => onChangeSelect(value)}
            value={languageTypeCode}
            options={languageLanguages?.map((language: ILanguageType) => ({
              value: language.code,
              label: `${language.name} (${language.code})`,
            }))}
          />
        ),
      },
      {
        key: 'theme',
        label: (
          <Switch
            checkedChildren="🌙"
            unCheckedChildren="☀️"
            checked={isDark}
            onChange={toggleTheme}
          />
        ),
      },
    ]);

    setMenuItems(menuItemsUE);
  }, [
    userIsLoggedIn,
    withdrawalReceivedCount,
    languageLanguages,
    languageTypeCode,
    dispatch,
    onChangeSelect,
    isDark,
    toggleTheme,
  ]);

  return <Menu mode="horizontal" items={menuItems} />;
}

export default Topbar;
